import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { PageHeader, Loading } from "@/components/Common";
import { EUR, EURc, pnlColor, fmtDate } from "@/lib/format";
import api from "@/lib/api";

const DOW = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function cellColor(pnl, max) {
  if (pnl === 0) return "rgba(255,255,255,0.02)";
  const intensity = Math.min(1, Math.abs(pnl) / (max || 1));
  const alpha = 0.15 + intensity * 0.55;
  return pnl > 0 ? `rgba(16,185,129,${alpha})` : `rgba(239,68,68,${alpha})`;
}

export default function Calendar() {
  const [month, setMonth] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [selected, setSelected] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ["calendar"], queryFn: () => api.get("/calendar").then((r) => r.data) });
  const { data: dayTrades } = useQuery({
    queryKey: ["day-trades", selected],
    queryFn: () => api.get(`/trades?date_from=${selected}&date_to=${selected}`).then((r) => r.data),
    enabled: !!selected,
  });

  const byDate = useMemo(() => {
    const map = {};
    (data?.days || []).forEach((d) => (map[d.date] = d));
    return map;
  }, [data]);

  const maxAbs = useMemo(() => Math.max(1, ...(data?.days || []).map((d) => Math.abs(d.pnl))), [data]);

  const grid = useMemo(() => {
    const first = new Date(month.y, month.m, 1);
    const startDow = (first.getDay() + 6) % 7; // Monday=0
    const days = new Date(month.y, month.m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= days; d++) {
      const ds = `${month.y}-${String(month.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push({ d, ds, info: byDate[ds] });
    }
    return cells;
  }, [month, byDate]);

  if (isLoading || !data) return <Loading />;

  const monthName = new Date(month.y, month.m).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const nav = (dir) => setMonth((s) => {
    let m = s.m + dir, y = s.y;
    if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; }
    return { y, m };
  });

  const stats = [
    { l: "Best Day", v: data.best_day ? EURc(data.best_day.pnl) : "—", tone: "profit", sub: data.best_day && fmtDate(data.best_day.date) },
    { l: "Worst Day", v: data.worst_day ? EURc(data.worst_day.pnl) : "—", tone: "loss", sub: data.worst_day && fmtDate(data.worst_day.date) },
    { l: "Media Giornaliera", v: EURc(data.avg_daily_pnl), tone: data.avg_daily_pnl >= 0 ? "profit" : "loss" },
    { l: "Giorni Positivi", v: `${data.positive_days}`, sub: `${data.positive_pct}%` },
    { l: "Giorni Negativi", v: `${data.negative_days}` },
  ];

  return (
    <div data-testid="calendar-page">
      <PageHeader title="Trading Calendar" subtitle="Heatmap giornaliera di P&L, trade e win rate" />

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-5">
        {stats.map((s) => (
          <div key={s.l} className="dlx-card p-4">
            <div className="font-sans text-[10px] uppercase tracking-widest text-zinc-500">{s.l}</div>
            <div className={`mt-2 font-mono text-lg font-bold ${s.tone === "profit" ? "text-profit" : s.tone === "loss" ? "text-loss" : "text-white"}`}>{s.v}</div>
            {s.sub && <div className="mt-0.5 font-mono text-[10px] text-zinc-600">{s.sub}</div>}
          </div>
        ))}
      </div>

      <div className="dlx-card p-5 sm:p-6">
        <div className="mb-5 flex items-center justify-between">
          <button onClick={() => nav(-1)} data-testid="calendar-prev" className="rounded-md border border-white/10 p-2 text-zinc-400 hover:text-gold"><ChevronLeft size={16} /></button>
          <span className="font-serif text-xl capitalize text-white">{monthName}</span>
          <button onClick={() => nav(1)} data-testid="calendar-next" className="rounded-md border border-white/10 p-2 text-zinc-400 hover:text-gold"><ChevronRight size={16} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
          {DOW.map((d) => <div key={d} className="pb-1 text-center font-sans text-[10px] uppercase tracking-wider text-zinc-600">{d}</div>)}
          {grid.map((c, i) => c === null ? <div key={i} /> : (
            <button key={c.ds} onClick={() => c.info && setSelected(c.ds)} disabled={!c.info}
              data-testid={`calendar-day-${c.ds}`}
              className="aspect-square rounded-lg border border-white/5 p-1.5 text-left transition-all hover:border-gold/40 disabled:cursor-default"
              style={{ background: c.info ? cellColor(c.info.pnl, maxAbs) : "transparent" }}>
              <div className="font-mono text-[10px] text-zinc-500">{c.d}</div>
              {c.info && (
                <div className="mt-0.5">
                  <div className={`font-mono text-[10px] font-semibold sm:text-xs ${c.info.pnl >= 0 ? "text-profit" : "text-loss"}`}>{EURc(c.info.pnl)}</div>
                  <div className="hidden font-mono text-[9px] text-zinc-500 sm:block">{c.info.count}t · {c.info.win_rate}%</div>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setSelected(null)} className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" />
            <motion.div initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
              className="fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-2xl -translate-x-1/2 -translate-y-1/2 dlx-card p-6" data-testid="calendar-day-modal">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <div className="font-serif text-xl text-white">{fmtDate(selected)}</div>
                  <div className="font-mono text-xs text-zinc-500">{dayTrades?.count ?? 0} trade</div>
                </div>
                <button onClick={() => setSelected(null)} className="text-zinc-500 hover:text-white"><X size={18} /></button>
              </div>
              <div className="max-h-[50vh] space-y-2 overflow-y-auto">
                {(dayTrades?.trades || []).map((t) => (
                  <div key={t.id} className="flex items-center justify-between rounded-lg border border-white/5 bg-white/[0.02] px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      <span className="font-sans text-sm font-medium text-white">{t.symbol}</span>
                      <span className="font-mono text-[10px]" style={{ color: t.direction === "BUY" ? "#10B981" : "#EF4444" }}>{t.direction}</span>
                      <span className="rounded-full border border-gold/20 px-2 py-0.5 font-sans text-[10px] text-gold">{t.setup}</span>
                    </div>
                    <span className={`font-mono text-sm font-semibold ${pnlColor(t.net_profit)}`}>{EURc(t.net_profit)}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
